import type { ComputedSection, ComputedTestRow, RichBlock, TemplateDoc } from '@quickreport/domain';

/** The JSON contract between TypeScript and report.typ. Keep both in step. */
export interface ReportData {
  meta: {
    title: string;
    reportId: string;
    patientName: string;
    /** Sign-off time in UTC; becomes the PDF's creation date. */
    date: { year: number; month: number; day: number; hour: number; minute: number };
  };
  lab: {
    name: string;
    tagline?: string;
    /** Where the name sits relative to the logo; "right" when absent. */
    nameAt?: 'above' | 'below' | 'left' | 'right' | 'hidden';
    address: string;
    phones: string;
    email?: string;
    accent: string;
    logo?: string;
    disclaimer?: string;
    registrations: { label: string; value: string }[];
  };
  patient: { left: [string, string][]; right: [string, string][] };
  sections: PrintSection[];
  signatories: {
    name: string;
    qualification: string;
    designation?: string;
    regNo?: string;
    signature?: string;
  }[];
  qr?: { path: string; code: string };
  /** The lab's comments under the results, already read into data (never markup). */
  comments?: RichBlock[];
  options: {
    letterhead: boolean;
    /** Which printed items to include (see PRINT_ITEMS in the domain package). Unset: all of them. */
    print?: Record<string, boolean>;
    topMarginMm: number;
    bottomMarginMm: number;
    signaturesOn: 'every-page' | 'last-page';
    showEndOfReport: boolean;
    /** Size of each signature block in mm: the space for the signature, and the block's width. */
    signatureHeightMm?: number;
    signatureWidthMm?: number;
    /** A small line printed at the foot of every page (the LabView contact line). */
    labviewLine?: string;
    /** Large pale text behind the page, e.g. PARTIAL or REVOKED. */
    watermark?: string;
    /** Virtual path of a full-page picture drawn behind every page (the lab's letterhead). */
    background?: string;
    /**
     * Print "End of Report" in the footer of the last page rather than after the results, so it
     * can never be the only thing on an extra page. Set with a letterhead picture, whose margins
     * are tight.
     */
    endInFooter?: boolean;
  };
}

export interface PrintColumn {
  id: string;
  role: string;
  label: string;
  width: number;
}

export type PrintRow =
  | { kind: 'section' | 'note'; text: string }
  | { kind: 'narrative'; title?: string; text: string }
  | {
      kind: 'test';
      label: string;
      method?: string;
      calculated: boolean;
      result: string;
      flag: string | null;
      band?: string;
      bold: boolean;
      cells: Record<string, string>;
      /** How this out-of-range value prints, only when it is not the usual bold red. */
      style?: { bold: boolean; color: string };
    };

export interface PrintSection {
  title: string;
  department?: string;
  newPage?: boolean;
  performedAt?: string;
  interpretation?: string;
  columns: PrintColumn[];
  rows: PrintRow[];
}

const DEFAULT_WIDTH: Record<string, number> = {
  investigation: 3.4,
  result: 1.3,
  flag: 0.45,
  ref_min: 1.2,
  ref_max: 1.2,
  ref_range: 2.2,
  unit: 1.2,
  method: 1.6,
  custom: 1.4,
};

/**
 * Lay out one computed template section for print: merge Min/Max into one reference column
 * when the template asks, add a narrow flag column after Result, and pre-render every cell.
 */
export function toPrintSection(
  doc: TemplateDoc,
  computed: ComputedSection,
  extra: {
    performedAt?: string;
    /** The look of an out-of-range value for a row, or nothing for the usual bold red. */
    abnormalStyle?: (rowId: string) => { bold: boolean; color: string } | undefined;
  } = {},
): PrintSection {
  let columns: PrintColumn[] = doc.columns.map((c) => ({
    id: c.id,
    role: c.role,
    label: c.label,
    width: c.width ?? DEFAULT_WIDTH[c.role] ?? 1.4,
  }));

  if (
    doc.print.mergeRefColumns &&
    columns.some((c) => c.role === 'ref_min' || c.role === 'ref_max')
  ) {
    const at = columns.findIndex((c) => c.role === 'ref_min' || c.role === 'ref_max');
    columns = columns.filter((c) => c.role !== 'ref_min' && c.role !== 'ref_max');
    columns.splice(at, 0, { id: '__ref', role: 'ref_range', label: 'Reference Range', width: 2.2 });
  }
  if (!columns.some((c) => c.role === 'flag')) {
    const at = columns.findIndex((c) => c.role === 'result');
    columns.splice(at + 1, 0, {
      id: '__flag',
      role: 'flag',
      label: '',
      width: DEFAULT_WIDTH.flag as number,
    });
  }

  const rows: PrintRow[] = [];
  for (const r of computed.rows) {
    if (r.kind !== 'test') {
      rows.push(
        r.kind === 'narrative'
          ? { kind: 'narrative', text: r.text }
          : { kind: r.kind, text: r.text },
      );
      continue;
    }
    if (r.row.hideIfEmpty && r.display === '') continue;
    rows.push(toPrintRow(r, columns, extra.abnormalStyle?.(r.row.id)));
  }

  return {
    title: doc.name,
    ...(doc.department ? { department: doc.department } : {}),
    ...(doc.print.newPage ? { newPage: true } : {}),
    ...(extra.performedAt ? { performedAt: extra.performedAt } : {}),
    ...(doc.interpretation ? { interpretation: doc.interpretation } : {}),
    columns,
    rows,
  };
}

function toPrintRow(
  r: ComputedTestRow,
  columns: PrintColumn[],
  style?: { bold: boolean; color: string },
): PrintRow {
  const cells: Record<string, string> = {};
  for (const c of columns) {
    switch (c.role) {
      case 'ref_range':
        cells[c.id] = r.refText;
        break;
      case 'ref_min':
        cells[c.id] = r.refMin;
        break;
      case 'ref_max':
        cells[c.id] = r.refMax;
        break;
      case 'unit':
        cells[c.id] = r.row.unit ?? '';
        break;
      case 'method':
        cells[c.id] = r.row.method ?? '';
        break;
      case 'custom':
        cells[c.id] = r.row.cells?.[c.id] ?? '';
        break;
    }
  }
  const hasMethodColumn = columns.some((c) => c.role === 'method');
  return {
    kind: 'test',
    label: r.row.label,
    ...(r.row.method && !hasMethodColumn ? { method: r.row.method } : {}),
    calculated: r.calculated,
    result: r.display,
    flag: r.flag.code,
    ...(r.flag.band ? { band: r.flag.band } : {}),
    bold: Boolean(r.row.printBold),
    cells,
    ...(style && r.flag.code ? { style } : {}),
  };
}

/** Print a department heading only where the department changes, as labs do. */
export function withDepartmentHeadings(sections: PrintSection[]): PrintSection[] {
  let previous: string | undefined;
  return sections.map((s) => {
    const same = s.department !== undefined && s.department === previous;
    previous = s.department;
    if (!same) return s;
    const { department: _, ...rest } = s;
    return rest;
  });
}
